import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { FormInstance, PaginationProps } from 'antd';
import { SortField } from '../queryClient';
import {
  countActiveFilters,
  searchParamToFilterValue,
  useDirectoryNavigation,
  usePageSizeOptions,
} from '../utils';
import { serializeSort, transformFilter } from './filterTransformer/default';
import { FilterTransformer, SortSerializer } from './filterTransformer/types';

export interface DirectoryOptions<
  QueryFilterKey extends string = string,
  QuerySortKey extends string = QueryFilterKey,
> {
  queryFilterKeys: QueryFilterKey[];
  initialFilters?: Partial<Record<QueryFilterKey, any>>;
  initialSort?: SortingState;
  initialPagination?: PaginationProps;
  serializeFilter?: FilterTransformer<string, QueryFilterKey>;
  deserializeFilter?: FilterTransformer<QueryFilterKey, string>;
  serializeSort?: SortSerializer<QuerySortKey>;
  sidePanelFilterRef?: RefObject<FormInstance<
    Partial<Record<QueryFilterKey, any>>
  > | null>;
  arrayFields?: QueryFilterKey[];
  numericFields?: QueryFilterKey[];
  booleanFields?: QueryFilterKey[];
}

export const useDirectory = <
  QueryFilterKey extends string = string,
  QuerySortKey extends string = QueryFilterKey,
>(
  options: DirectoryOptions<QueryFilterKey, QuerySortKey>,
) => {
  const {
    queryFilterKeys,
    initialFilters,
    initialSort = [],
    initialPagination,
    serializeFilter,
    deserializeFilter,
    serializeSort,
    sidePanelFilterRef,
    arrayFields,
    numericFields,
    booleanFields,
  } = options;

  const serializeFilterRef = useRef(serializeFilter);
  useEffect(() => {
    serializeFilterRef.current = serializeFilter;
  }, [serializeFilter]);

  const deserializeFilterRef = useRef(deserializeFilter);
  useEffect(() => {
    deserializeFilterRef.current = deserializeFilter;
  }, [deserializeFilter]);

  const serializeSortRef = useRef(serializeSort);
  useEffect(() => {
    serializeSortRef.current = serializeSort;
  }, [serializeSort]);

  const initialFiltersRef = useRef(initialFilters);
  const initialPaginationRef = useRef(initialPagination);
  const pageSizeOptions = usePageSizeOptions();

  const parseUrlSearchParam = useCallback(
    (key: string, value: string | null) =>
      searchParamToFilterValue(
        key,
        value,
        arrayFields,
        numericFields,
        booleanFields,
      ),
    [arrayFields, numericFields, booleanFields],
  );

  const setSidePanelFilters = useCallback(
    (queryFilters?: Partial<Record<QueryFilterKey, any>>) => {
      if (queryFilters) {
        sidePanelFilterRef?.current?.setFieldsValue(
          transformFilters(queryFilters, deserializeFilterRef.current),
        );
      }
    },
    [sidePanelFilterRef, deserializeFilterRef],
  );

  const handleInitialQueryFilters = useCallback(
    (initialQueryFilters: Partial<Record<QueryFilterKey, any>>) => {
      setColumnFilters(
        getColumnFiltersFromQueryFilters(
          initialQueryFilters,
          deserializeFilterRef.current,
        ),
      );
      setSidePanelFilters(initialQueryFilters);
    },
    [setSidePanelFilters, deserializeFilterRef],
  );

  const {
    queryFilters,
    setQueryFilters,
    handlePageChange,
    handlePageSizeChange,
    handleTotalChange,
    pageIndex,
    pagination,
  } = useDirectoryNavigation(
    queryFilterKeys,
    initialFiltersRef.current,
    initialPaginationRef.current,
    parseUrlSearchParam,
    handleInitialQueryFilters,
  );

  const [sortingState, setSortingState] = useState<SortingState>(initialSort);

  const sort = useMemo(
    () =>
      getQuerySortFromColumnSort<QuerySortKey>(
        sortingState,
        serializeSortRef.current,
      ),
    [sortingState],
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    getColumnFiltersFromQueryFilters(
      { ...initialFiltersRef.current, ...queryFilters },
      deserializeFilterRef.current,
    ),
  );

  const handleSearch = useCallback(
    (value: string) => {
      setQueryFilters({ ...queryFilters, search: value || undefined });
    },
    [queryFilters, setQueryFilters],
  );

  const handleTableFilter: OnChangeFn<ColumnFiltersState> = useCallback(
    (updaterOrValue) => {
      setColumnFilters((current) => {
        const next =
          updaterOrValue instanceof Function
            ? updaterOrValue(current)
            : updaterOrValue;

        const nextQueryFilters = getQueryFiltersFromColumnFilters<
          QueryFilterKey,
          Partial<Record<QueryFilterKey, any>>
        >(next, serializeFilterRef.current);
        setQueryFilters(nextQueryFilters);
        setSidePanelFilters(nextQueryFilters);

        return next;
      });
    },
    [setQueryFilters, setSidePanelFilters, serializeFilterRef],
  );

  const handleSidePanelFilter = useCallback(
    (sidePanelFilters: Partial<Record<QueryFilterKey, any>>) => {
      const nextQueryFilters = {
        ...queryFilters,
        ...transformFilters<
          Partial<Record<QueryFilterKey, any>>,
          string,
          QueryFilterKey
        >(sidePanelFilters, (id, value, allFilters) =>
          (serializeFilterRef.current || transformFilter)(
            id,
            (arrayFields?.includes(id as QueryFilterKey) && !value?.length) ||
              (booleanFields?.includes(id as QueryFilterKey) &&
                typeof value !== 'boolean')
              ? undefined
              : value,
            allFilters,
          ),
        ),
      };
      setQueryFilters(nextQueryFilters);
      setColumnFilters(
        getColumnFiltersFromQueryFilters(
          nextQueryFilters,
          deserializeFilterRef.current,
        ),
      );
    },
    [
      queryFilters,
      setQueryFilters,
      serializeFilterRef,
      arrayFields,
      booleanFields,
    ],
  );

  const handleClearFilters = useCallback(() => {
    setColumnFilters([]);
    setQueryFilters({});
    setSidePanelFilters({});
  }, [setQueryFilters, setSidePanelFilters]);

  const activeFiltersCount = useMemo(
    () => countActiveFilters(queryFilters),
    [queryFilters],
  );

  const headerProps = useMemo(
    () => ({
      onFilterClear: handleClearFilters,
      filtersCount: activeFiltersCount,
      onSearch: handleSearch,
      defaultValue:
        ('search' in queryFilters &&
          typeof queryFilters.search === 'string' &&
          queryFilters.search) ||
        undefined,
    }),
    [
      handleClearFilters,
      activeFiltersCount,
      handleSearch,
      (queryFilters as any).search,
    ],
  );

  const sidePanelFilterProps = useMemo(
    () => ({
      onClear: handleClearFilters,
      onFilter: handleSidePanelFilter,
    }),
    [handleClearFilters, handleSidePanelFilter],
  );

  const tableProps = useMemo(
    () => ({
      filters: columnFilters,
      onFilter: handleTableFilter,
      sort: sortingState,
      onSort: setSortingState,
    }),
    [columnFilters, handleTableFilter, sortingState, setSortingState],
  );

  const paginationProps = useMemo(
    () => ({
      ...pagination,
      pageSizeOptions,
      onChange: handlePageChange,
      onShowSizeChange: (_page: number | undefined, pageSize: number) =>
        handlePageSizeChange(1, pageSize),
    }),
    [pagination, pageSizeOptions, handlePageChange, handlePageSizeChange],
  );

  const queryParameters = useMemo(
    () => ({
      page: pageIndex,
      size: pagination.pageSize,
      sort,
      ...queryFilters,
    }),
    [pageIndex, pagination.pageSize, sort, queryFilters],
  );

  return useMemo(
    () => ({
      headerProps,
      sidePanelFilterProps,
      tableProps,
      paginationProps,
      queryParameters,
      setQueryFilters,
      setTotal: handleTotalChange,
      setTableFilters: handleTableFilter,
    }),
    [
      headerProps,
      sidePanelFilterProps,
      tableProps,
      paginationProps,
      queryParameters,
      setQueryFilters,
      handleTotalChange,
      handleTableFilter,
    ],
  );
};

/**
 * Takes an object of filters and gives back another object of filters.
 * Calls given `transform` function for each filter in the given object.
 *
 * @param filters
 * @param transform
 */
const transformFilters = <
  QueryFilters extends Partial<Record<string, any>> = Partial<
    Record<string, any>
  >,
  K1 extends string = string,
  K2 extends string = string,
>(
  filters: Partial<Record<K1, any>>,
  transform: FilterTransformer<K1, K2> = transformFilter,
) =>
  Object.fromEntries(
    Object.entries(filters).flatMap(([id, value]) =>
      Object.entries(transform(id as K1, value, filters)),
    ),
  ) as QueryFilters;

/**
 * Takes an array of column filters and gives back an object of filters.
 * Calls given `transform` function for each filter in the given array.
 *
 * @param columnFilters
 * @param transform
 */
const getQueryFiltersFromColumnFilters = <
  K extends string = string,
  F extends Partial<Record<K, any>> = Partial<Record<K, any>>,
>(
  columnFilters: ColumnFiltersState,
  transform: FilterTransformer<string, K> = transformFilter,
) =>
  columnFilters.reduce(
    (queryFilters, { id, value }) => ({
      ...queryFilters,
      ...transform(id, value, columnFilters),
    }),
    {} as F,
  );

/**
 * Takes an object of filters and gives back an array of column filters.
 * Calls given `transform` function for each filter in the given object.
 *
 * @param queryFilters
 * @param transform
 */
const getColumnFiltersFromQueryFilters = <K extends string = string>(
  queryFilters: Partial<Record<K, any>>,
  transform: FilterTransformer<K> = transformFilter,
): ColumnFiltersState =>
  Object.entries(queryFilters)
    .flatMap((entry) =>
      Object.entries(transform(...(entry as [K, any]), queryFilters)),
    )
    .filter(([, value]) =>
      Array.isArray(value)
        ? value.filter(isActiveFilterValue)?.length
        : isActiveFilterValue(value),
    )
    .map(([id, value]) => ({ id, value }));

const isActiveFilterValue = (value: any) =>
  typeof value !== 'undefined' && value !== '';

/**
 *
 * @param sortingState
 * @param transform
 */
const getQuerySortFromColumnSort = <T extends string = string>(
  sortingState: SortingState,
  transform: SortSerializer<T> = serializeSort<T>,
): SortField<T>[] =>
  sortingState
    .map((columnSort) => transform(columnSort, sortingState))
    .flat() as SortField<T>[];
